// Email & contact configuration for an operator org (Neel, Aug 20: operators
// set up their email configuration; interim white-label). The contact email
// becomes the reply-to and the contact info on every tenant mail. Only the
// org's super_admin may save (RLS enforces it; the UI just says so).
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useOperator } from "@/hooks/useOperator";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail } from "lucide-react";

export function OperatorContactCard() {
  const { operator } = useOperator();
  const queryClient = useQueryClient();
  const [contactEmail, setContactEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setContactEmail(operator?.contact_email ?? "");
  }, [operator?.contact_email]);

  if (!operator) return null;
  const isOrgSuperAdmin = operator.member_role === "super_admin";

  const save = async () => {
    const trimmed = contactEmail.trim();
    if (trimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase
        .from("operators")
        .update({ contact_email: trimmed || null })
        .eq("id", operator.id);
      if (error) throw error;
      toast.success("Contact email saved");
      queryClient.invalidateQueries({ queryKey: ["operator-org"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-4 w-4" aria-hidden />
          Email &amp; contact
        </CardTitle>
        <CardDescription>
          Tenant emails are sent from noreply@coliviq.de under your name —
          replies and the contact details inside them go to this address. Custom
          sender domains come with the white-label package.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="op-contact-email">Contact email</Label>
          <Input
            id="op-contact-email"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="office@yourcompany.de"
            disabled={!isOrgSuperAdmin}
          />
        </div>
        {isOrgSuperAdmin ? (
          <Button onClick={save} disabled={saving} size="sm">
            {saving ? "Saving…" : "Save"}
          </Button>
        ) : (
          <p className="text-sm text-muted-foreground">
            Only your organization's super admin can change this.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
